import { AnyAction, PayloadAction, createSlice } from '@reduxjs/toolkit';
import React, {
  Context,
  Dispatch,
  FC,
  MutableRefObject,
  createContext,
  useEffect,
} from 'react';
import { filterDuplicateTasks } from 'components/organisms/sprint-planning/utils/utils';
import { useLatestRef } from 'hooks/use-latest';
import { logger } from 'services';
import {
  ColumnPaginationAction,
  DropParams,
  GetRowAndColumnForTaskIdResponse,
  GetRowAndColumnForTaskIdSuccessResponse,
  Id,
  InternalKanbanBoardState,
  KanbanSwimlanes,
  LayoutFetch,
  LayoutFetchResponse,
  PurgeAction,
  SocketPaginationParams,
  Task,
  UpdateSwimlaneParams,
  UpdateSwimlaneRequestStatus,
} from './type';

type KanbanActions = {
  handleDrop: (params: DropParams) => void;
  purgeData: (params: PurgeAction) => void;
  resetBoard: () => void;
  updatePaginatedSwimlane: (params: UpdateSwimlaneParams) => void;
  updateSwimlaneRequestStatus: (params: UpdateSwimlaneRequestStatus) => void;
  updatePaginatedColumn: (params: ColumnPaginationAction) => void;
  handleSocketUpdate: (params: SocketPaginationParams) => void;
  deleteTask: (params: { taskId: Id }) => void;
  filterStatuses: (params: { statusesToShow: number[] }) => void;
  updateSwimlaneForSprint: (params: UpdateSwimlaneParams) => void;
};
export interface KanbanContextParams<TaskDetails extends { id: Id } = any> {
  dragTask: Task<TaskDetails> | null;
  setTask: (task: Task<TaskDetails> | null) => void;
  handleDragAndDrop: (
    params: DropParams<TaskDetails>,
  ) => Promise<GetRowAndColumnForTaskIdResponse<TaskDetails>>;
  kanbanState: InternalKanbanBoardState<TaskDetails> | null;
  dragRef: MutableRefObject<any> | null;
  kanbanActions: KanbanActions;
  readOnly?: boolean;
}

const handleDrop = (
  state: InternalKanbanBoardState,
  action: PayloadAction<
    DropParams & {
      isUpdating: boolean;
    }
  >,
) => {
  const { from, to, task, isUpdating } = action.payload;

  state.data[from.swimlaneId].cols[from.colId].tasks = state.data[
    from.swimlaneId
  ].cols[from.colId].tasks.filter((t) => t.id !== task.id);
  state.data[from.swimlaneId].cols[from.colId].count -= 1;
  state.data[to.swimlaneId].cols[to.colId].count += 1;
  state.data[to.swimlaneId].cols[to.colId].tasks.unshift({
    ...task,
    isDropped: true,
    colId: to.colId,
    swimlaneId: to.swimlaneId,
    isUpdating,
  });
  const acrossSwimlanes = from.swimlaneId !== to.swimlaneId;

  if (acrossSwimlanes) {
    state.data[from.swimlaneId].count -= 1;
    state.data[to.swimlaneId].count += 1;
  }

  return state;
};

const upsertTaskInCells = <T extends { id: Id }>(
  state: InternalKanbanBoardState,
  action: PayloadAction<GetRowAndColumnForTaskIdSuccessResponse<T>>,
) => {
  const { values, task } = action.payload;

  values.forEach(({ swimlaneId, columnIds }) => {
    columnIds.forEach((columnId) => {
      const existingTask = state.data[swimlaneId].cols[columnId].tasks.find(
        (t) => t.id === task.id,
      );

      if (existingTask) {
        existingTask.isDropped = true;
        existingTask.extra = task.extra;
        existingTask.label = task.label;
        existingTask.isUpdating = false;
      } else {
        state.data[swimlaneId].cols[columnId].tasks.unshift({
          colId: columnId,
          swimlaneId,
          id: task.id,
          isDropped: true,
          extra: task.extra,
          label: task.label,
          isUpdating: false,
        });
      }
    });
  });

  return state;
};

const deleteTask = (
  state: InternalKanbanBoardState,
  action: PayloadAction<{ taskId: Id }>,
) => {
  Object.keys(state.data).forEach((swimlaneId) => {
    Object.keys(state.data[swimlaneId].cols).forEach((colId) => {
      const taskToBeDeleted = state.data[swimlaneId].cols[colId].tasks.filter(
        (task) => task.id === action.payload.taskId,
      );

      if (taskToBeDeleted.length) {
        state.data[swimlaneId].cols[colId].count -= taskToBeDeleted.length;
        state.data[swimlaneId].count -= taskToBeDeleted.length;
        state.data[swimlaneId].cols[colId].tasks = state.data[swimlaneId].cols[
          colId
        ].tasks.filter((task) => task.id !== action.payload.taskId);
      }
    });
  });

  return state;
};

const purgeData = (
  state: InternalKanbanBoardState,
  actions: PayloadAction<PurgeAction>,
) => {
  if (!state.isPaginated) {
    return state;
  }
  const { inView, endOffset, startOffset } = actions.payload;
  const swimlaneIds = inView.swimlaneIds.map(String);
  const allSwimlanes = Object.keys(state.data);
  const purgeNotInView = (id: string): void => {
    logger.debug('purging task', { id, swimlaneIds });
    const isInView = swimlaneIds.includes(id);

    if (isInView) {
      return;
    }
    const { cols } = state.data[id];
    const allCollIds = Object.keys(cols);

    allCollIds.forEach((colId) => {
      const col = cols[colId];

      if (col) {
        // if start and end aren't specified, then we purge all
        if (startOffset == null && endOffset == null) {
          col.backendPagination.memoryInBlock = 0;
          col.tasks = col.tasks.filter((task) => task.isDropped);

          return;
        }
        col.backendPagination.memoryInBlock = 1;
        // if start and end are specified then we purge only those that are not in view
        col.tasks = col.tasks.filter((_, idx) => {
          if (startOffset! <= idx && idx <= endOffset!) {
            return true;
          }

          return false;
        });
      }
    });
  };

  allSwimlanes.forEach(purgeNotInView);

  return state;
};

const initLayout = <T extends { id: Id }, R extends { id: Id }>(
  state: InternalKanbanBoardState,
  actions: PayloadAction<LayoutFetchResponse<T, R>>,
) => {
  state.isPaginated = true;
  actions.payload.swimlane.forEach((swimlane) => {
    state.data[swimlane.id] = {
      count: swimlane.count,
      cols: swimlane.column.reduce((acc, column) => {
        acc[column.id] = {
          count: column.count,
          tasks: [],
          extra: column.extra,
          id: column.id,
          label: column.label,
          networkState: 'idle',
          backendPagination: {
            blockSize: 10,
            memoryInBlock: 0,
          },
        };

        return acc;
      }, {} as KanbanSwimlanes['cols']),
      label: swimlane.label,
      extra: swimlane.extra,
      id: swimlane.id,
      networkState: 'idle',
    };
  });
  state.orderSwimlaneIds = actions.payload.swimlane.map(
    (swimlane) => swimlane.id,
  );
  state.orderColIds = actions.payload.orderColIds;
  state.filteredCols = actions.payload.filteredCols;

  return state;
};

const initBoard = <T extends { id: Id }>(
  state: InternalKanbanBoardState,
  actions: PayloadAction<{ data: UpdateSwimlaneParams<T>['data'] }>,
) => {
  state.isPaginated = false;
  const orderSwimlaneIds = Object.keys(actions.payload.data);
  const orderColIds = Object.keys(
    actions.payload.data[orderSwimlaneIds[0]].cols,
  );

  state.data = Object.keys(actions.payload.data).reduce((acc, swimlaneId) => {
    const swimlane = actions.payload.data[swimlaneId];

    acc[swimlane.id] = {
      ...swimlane,
      count: swimlane.count,
      cols: Object.keys(swimlane.cols).reduce((colAcc, columnId) => {
        const column = swimlane.cols[columnId];

        colAcc[column.id] = {
          ...column,
          networkState: 'idle',
          backendPagination: {
            memoryInBlock: 0,
            blockSize: 0,
          },
        };

        return colAcc;
      }, {}),
      networkState: 'idle',
      filteredCols: [],
    };

    return acc;
  }, {});
  state.orderColIds = orderColIds;
  state.orderSwimlaneIds = orderSwimlaneIds;

  return state;
};

const updatePaginatedSwimlane = <T extends { id: Id }>(
  state: InternalKanbanBoardState,
  actions: PayloadAction<UpdateSwimlaneParams<T>>,
) => {
  const swimlanes = actions.payload;
  const swimlanesIdsToUpdate = Object.keys(actions.payload);

  swimlanesIdsToUpdate.forEach((id) => {
    if (state.data[id]) {
      state.data[id].networkState = 'success';

      const colIds = Object.keys(swimlanes[id].cols);

      colIds.forEach((colId) => {
        if (state.data[id].cols[colId]) {
          const updatedTasksList = [
            ...(state.data[id].cols[colId].tasks.filter(
              (task) => task.isDropped,
            ) || []),
            ...swimlanes[id].cols[colId].tasks.map((task) => ({
              ...task,
              isUpdating: false,
            })),
          ];

          const uniqueTasksList = filterDuplicateTasks(updatedTasksList);

          state.data[id].cols[colId].extra = swimlanes[id].cols[colId].extra;
          state.data[id].cols[colId].extra = swimlanes[id].cols[colId].extra;
          state.data[id].cols[colId].id = swimlanes[id].cols[colId].id;
          state.data[id].cols[colId].label = swimlanes[id].cols[colId].label;
          state.data[id].cols[colId].tasks = uniqueTasksList;
          state.data[id].cols[colId].networkState = 'success';

          state.data[id].cols[colId].backendPagination = {
            blockSize: 10,
            memoryInBlock: 1,
          };
        }
      });
    }
  });

  return state;
};

const updatePaginatedColumn = <T extends { id: Id }>(
  state: InternalKanbanBoardState,
  actions: PayloadAction<ColumnPaginationAction<T>>,
) => {
  const { swimlaneId, tasks, columnId } = actions.payload;
  const existingTaskId = state.data[swimlaneId].cols[columnId].tasks.map(
    (t) => t.id,
  );
  const uniqueTasks = tasks.filter((task) => !existingTaskId.includes(task.id));

  state.data[swimlaneId].cols[columnId].backendPagination.memoryInBlock += 1;
  state.data[swimlaneId].cols[columnId].tasks = [
    ...state.data[swimlaneId].cols[columnId].tasks.map(
      (task) =>
        ({
          ...task,
        } as Task<T>),
    ),
    ...uniqueTasks.map((t) => ({
      ...t,
      isUpdating: false,
      isDropped: false,
    })),
  ];

  return state;
};

const updateSwimlaneFetching = (
  state: InternalKanbanBoardState,
  actions: PayloadAction<UpdateSwimlaneRequestStatus>,
) => {
  const { swimlaneIds, status } = actions.payload;

  swimlaneIds.forEach((id) => {
    state.data[id].networkState = status;
  });

  return state;
};

const resetBoard = (state: InternalKanbanBoardState) => {
  state = {
    data: {},
    orderSwimlaneIds: [],
    orderColIds: [],
    isPaginated: null,
    filteredCols: [],
  };

  return state;
};

const handleSocketUpdate = (
  state: InternalKanbanBoardState,
  actions: PayloadAction<SocketPaginationParams>,
) => {
  const { swimlaneData: newSwimlaneData, taskDetails } = actions.payload;
  let isUpdated = false;

  Object.keys(state.data).forEach((swimlaneId) => {
    const allCols = Object.keys(state.data[swimlaneId].cols);

    allCols.forEach((colId) => {
      const existingTask = state.data[swimlaneId].cols[colId].tasks.find(
        (t) => t.id === taskDetails.id,
      );
      const doesThisExistInWs =
        newSwimlaneData[swimlaneId]?.columnData[colId] != null;

      if (doesThisExistInWs && existingTask) {
        existingTask.extra = taskDetails;
        isUpdated = true;
      } else if (!doesThisExistInWs && existingTask) {
        state.data[swimlaneId].cols[colId].tasks = state.data[swimlaneId].cols[
          colId
        ].tasks.filter((t) => t.id !== taskDetails.id);
        state.data[swimlaneId].cols[colId].count -= 1;
        state.data[swimlaneId].count -= 1;
      }
    });
  });
  if (!isUpdated) {
    Object.keys(newSwimlaneData).forEach((swimlaneId) => {
      if (state.data[swimlaneId]) {
        Object.keys(newSwimlaneData[swimlaneId].columnData).forEach((colId) => {
          state.data[swimlaneId].cols[colId].tasks.unshift({
            colId,
            extra: taskDetails,
            id: taskDetails.id,
            isDropped: true,
            isUpdating: false,
            label: taskDetails.label,
            swimlaneId,
          });
          state.data[swimlaneId].cols[colId].count += 1;
          state.data[swimlaneId].count += 1;
        });
      }
    });
  }

  return state;
};

const updateSwimlaneForSprint = <T extends { id: Id }>(
  state: InternalKanbanBoardState,
  actions: PayloadAction<UpdateSwimlaneParams<T>>,
) => {
  const swimlanes = actions.payload;
  const swimlanesIdsToUpdate = Object.keys(actions.payload);

  swimlanesIdsToUpdate.forEach((id) => {
    if (state.data[id]) {
      /* Setting it to idle because intially new tasks are added and
      later when user scrolls existing tasks needs to be fetched. */
      state.data[id].networkState =
        state.data[id].networkState !== 'success' ? 'idle' : 'success';
      const colIds = Object.keys(swimlanes[id].cols);

      colIds.forEach((colId) => {
        if (state.data[id].cols[colId]) {
          const updatedTasksList = [
            ...state.data[id].cols[colId].tasks,
            ...swimlanes[id].cols[colId].tasks.map((task) => ({
              ...task,
              isUpdating: false,
            })),
          ];

          state.data[id].cols[colId].tasks =
            filterDuplicateTasks(updatedTasksList);
          state.data[id].cols[colId].count += swimlanes[id].cols[colId].count;
          state.data[id].cols[colId].networkState =
            state.data[id].networkState !== 'success' ? 'idle' : 'success';
        }
      });

      const totalRowCount = Object.keys(state.data[id].cols).reduce(
        (acc, value) => {
          return acc + state.data[id].cols[value].count;
        },
        0,
      );

      state.data[id].count = totalRowCount;
    }
  });

  return state;
};

const filterStatuses = (
  state: InternalKanbanBoardState,
  actions: PayloadAction<{ statusesToShow: number[] }>,
) => {
  const { statusesToShow } = actions.payload;

  return { ...state, filteredCols: statusesToShow };
};

export const kanbanSlice = createSlice({
  initialState: {
    data: {},
    orderSwimlaneIds: [],
    orderColIds: [],
    isPaginated: null,
    filteredCols: [],
  } as InternalKanbanBoardState,
  name: 'kanban-board-slice',
  reducers: {
    handleDrop,
    initLayout,
    initBoard,
    purgeData,
    updatePaginatedSwimlane,
    updateSwimlaneForSprint,
    updatePaginatedColumn,
    updateSwimlaneFetching,
    upsertTaskInCells,
    resetBoard,
    handleSocketUpdate,
    deleteTask,
    filterStatuses,
  },
});
export const stateKanbanActions = kanbanSlice.actions;
export const kanbanReducer = kanbanSlice.reducer;

export const useInit = (
  dispatch: Dispatch<AnyAction>,
  {
    layoutFetch,
    isPaginated,
  }: {
    layoutFetch: LayoutFetch | (() => Promise<UpdateSwimlaneParams<any>>);
    isPaginated: boolean;
  },
  actions: typeof kanbanSlice['actions'],
) => {
  const latestFetchDetails = useLatestRef(layoutFetch);

  useEffect(() => {
    if (isPaginated) {
      latestFetchDetails.current().then((state) => {
        dispatch(actions.initLayout(state));
      });
    } else {
      latestFetchDetails.current().then((state) => {
        dispatch(actions.initBoard({ data: state }));
      });
    }
  }, [actions, dispatch, isPaginated, latestFetchDetails]);
};

export const KanbanContext = createContext(
  null,
) as Context<KanbanContextParams | null>;

export function withKanbanContext<R>(
  Component: FC<R & KanbanContextParams>,
): FC<R> {
  return (props: R) => {
    return (
      <KanbanContext.Consumer>
        {(dndProps) => {
          if (dndProps === null) {
            throw new Error('KanbanContext is null');
          }

          return <Component {...props} {...dndProps} />;
        }}
      </KanbanContext.Consumer>
    );
  };
}
